import React from 'react';
import type { SettingsActivity, VersionInfo } from './types';

interface SettingsInfoSidebarProps {
  version: VersionInfo;
  activities: SettingsActivity[];
}

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm flex flex-col h-[220px]">
      <h2 className="text-xs font-medium text-slate-900 mb-3 shrink-0">{title}</h2>
      <div className="flex-1 flex flex-col overflow-hidden">
        {children}
      </div>
    </section>
  );
}

export function SettingsInfoSidebar({ version, activities }: SettingsInfoSidebarProps) {
  return (
    <aside className="grid gap-4 md:grid-cols-3">
      <Card title="Thông tin phiên bản">
        <div className="space-y-3 flex-1 flex flex-col">
          <div>
            <p className="text-xs text-slate-500">Phiên bản hiện tại</p>
            <p className="mt-0.5 text-lg font-medium text-emerald-700">{version.version}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Cập nhật lần cuối</p>
            <p className="mt-0.5 text-xs font-medium text-slate-800">{formatDateTime(version.lastUpdate)}</p>
          </div>
          <button type="button" className="inline-flex w-full h-9 items-center justify-center gap-2 rounded-md border border-slate-200 px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 transition mt-auto shrink-0">
            <i className="ri-refresh-line" />
            Kiểm tra cập nhật
          </button>
        </div>
      </Card>

      <Card title="Hỗ trợ & Trợ giúp">
        <div className="space-y-1 -mx-2 flex-1 flex flex-col overflow-y-auto pr-2 custom-scrollbar">
          {[
            { icon: 'ri-question-line', title: 'Trung tâm trợ giúp', text: 'Xem hướng dẫn và FAQ' },
            { icon: 'ri-mail-send-line', title: 'Gửi yêu cầu hỗ trợ', text: 'Chúng tôi sẽ phản hồi sớm' },
            { icon: 'ri-feedback-line', title: 'Phản hồi & góp ý', text: 'Giúp chúng tôi cải tiến' },
          ].map((item) => (
            <button key={item.title} type="button" className="flex w-full items-start gap-2.5 rounded-md p-2 text-left hover:bg-slate-50 transition shrink-0">
              <i className={`${item.icon} mt-0.5 text-base text-slate-400`} />
              <span className="space-y-0.5">
                <span className="block text-xs font-medium text-slate-800">{item.title}</span>
                <span className="block text-[11px] leading-tight text-slate-500">{item.text}</span>
              </span>
            </button>
          ))}
        </div>
      </Card>

      <Card title="Nhật ký hoạt động">
        <div className="space-y-3 flex-1 flex flex-col">
          {activities.length ? (
            <div className="space-y-3 border-l-2 border-slate-100 ml-2 pl-3">
              {activities.slice(0, 2).map((item) => (
                <div key={item.id} className="relative">
                  <div className="absolute -left-[17px] top-1 h-2 w-2 rounded-full bg-emerald-500 border-2 border-white" />
                  <div className="min-w-0 space-y-0.5">
                    <p className="text-xs font-medium text-slate-800 line-clamp-2">{item.actorName} {item.summary}</p>
                    <p className="text-[10px] text-slate-500">{formatDateTime(item.createdAt)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="rounded-md bg-slate-50 p-2 text-xs text-slate-500 text-center">Chưa có hoạt động.</p>
          )}
          <button type="button" className="w-full text-center mt-auto p-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-700 hover:underline">
            Xem toàn bộ lịch sử <i className="ri-arrow-right-line ml-1" />
          </button>
        </div>
      </Card>
    </aside>
  );
}
